// src/utils/configInheritance.ts
import { SdkConfig, INHERIT_CONFIG } from '../types/config';

/**
 * Resolves inheritance for plugin configurations
 * @param pluginConfig The plugin-specific configuration
 * @param sdkConfig The top-level SDK configuration
 * @param _pluginName The name of the plugin for logging
 * @returns Resolved configuration with inheritance applied
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolvePluginConfig<T extends Record<string, any>>(
  pluginConfig: T | undefined,
  sdkConfig: SdkConfig
): T {
  if (!pluginConfig) {
    return {} as T;
  }

  const resolvedConfig = { ...pluginConfig };

  // Iterate through all properties in the plugin config
  for (const [key, value] of Object.entries(pluginConfig)) {
    if (value === INHERIT_CONFIG) {
      // Look for the same key in the top-level SDK config
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inheritedValue = (sdkConfig as any)[key];

      if (inheritedValue !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (resolvedConfig as any)[key] = inheritedValue;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (resolvedConfig as any)[key] = undefined;
      }
    }
  }

  return resolvedConfig;
}

/**
 * Gets the resolved configuration for a specific plugin
 * @param sdkConfig The SDK configuration
 * @param _pluginName The name of the plugin
 * @returns The resolved plugin configuration
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPluginConfig<T extends Record<string, any>>(
  sdkConfig: SdkConfig,
  _pluginName: string
): T {
  const pluginConfig = sdkConfig.pluginConfig?.[_pluginName] as T | undefined;
  return resolvePluginConfig(pluginConfig, sdkConfig, _pluginName);
}

/**
 * Validates that inheritance values are properly configured
 * @param config The configuration object to validate
 * @param configName The name of the configuration for logging
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateInheritanceConfig(config: Record<string, any>, configName: string): void {
  for (const [key, value] of Object.entries(config)) {
    if (typeof value === 'object' && value !== null) {
      // Recursively validate nested objects
      validateInheritanceConfig(value, `${configName}.${key}`);
    } else if (value === INHERIT_CONFIG) {
      // Property is set to inherit from parent config
    }
  }
}

/**
 * Creates a deep clone of a configuration object, preserving inheritance markers
 * @param config The configuration to clone
 * @returns A deep clone of the configuration
 */
export function cloneConfigWithInheritance<T>(config: T): T {
  if (config === null || typeof config !== 'object') {
    return config;
  }

  if (typeof config === 'string' && config === INHERIT_CONFIG) {
    return config;
  }

  if (Array.isArray(config)) {
    return config.map(item => cloneConfigWithInheritance(item)) as unknown as T;
  }

  const cloned = {} as T;
  for (const [key, value] of Object.entries(config)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cloned as any)[key] = cloneConfigWithInheritance(value);
  }

  return cloned;
}

/**
 * Merges default configuration with user configuration, preserving inheritance
 * @param defaultConfig The default configuration
 * @param userConfig The user-provided configuration
 * @returns Merged configuration
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeConfigWithInheritance<T extends Record<string, any>>(
  defaultConfig: T,
  userConfig: Partial<T>
): T {
  const merged = { ...defaultConfig };

  for (const [key, value] of Object.entries(userConfig)) {
    if (value === INHERIT_CONFIG) {
      // Preserve inheritance marker
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[key] = INHERIT_CONFIG;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Deep merge objects
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const defaultValue = (defaultConfig as any)[key];
      if (
        typeof defaultValue === 'object' &&
        defaultValue !== null &&
        !Array.isArray(defaultValue)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (merged as any)[key] = mergeConfigWithInheritance(defaultValue, value);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (merged as any)[key] = cloneConfigWithInheritance(value);
      }
    } else {
      // Direct assignment for primitives and arrays
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[key] = value;
    }
  }

  return merged;
}
